import type { PdpKannibalisierung, PdpRanking, PdpRankingWert, PdpSuchdaten } from '@awf/shared';

/**
 * Portierung von Martins `renderRankingHtml` (Analyse DEV, Node `HTML Formular generieren`)
 * als reine Ableitung: Daten rein, Anzeigeobjekte raus -- das Rendern macht React
 * (Spec 2026-10-07, Abschnitt 4.3). Texte und Regeln wie dort, damit Formular und
 * Frontend dasselbe sagen.
 */

export type Ton = 'schlecht' | 'mittel' | 'gut';

export interface RankingTabelle {
  kopf: string[];
  /** Spaltenindizes, die rechtsbuendig stehen (Zahlen). */
  rechts: number[];
  zeilen: string[][];
}

export interface KannibalenZeile {
  position: string;
  herkunft: string;
  typ: string;
  fremdeDomain?: string;
  seite: { text: string; url?: string };
  istZiel: boolean;
}

export interface RankingAnzeige {
  status?: { text: string; ton: Ton };
  analyseText?: string;
  top3?: RankingTabelle;
  nonbrand?: RankingTabelle;
  gscHinweis?: string;
  identitaet?: { ueberschrift: string; tabelle: RankingTabelle };
  fehlRanking?: { produkt: string; url?: string };
  kannibalisierung?: { titel: string; risiko: boolean; zeilen: KannibalenZeile[] };
}

export function position(n: number | null): string {
  return n === null ? '–' : n.toFixed(1).replace('.', ',');
}

/** Fehlender Wert ist nicht null: ohne Search-Console-Historie steht "keine Daten", nicht "0". */
function anzahl(n: number | null): string {
  return n === null ? 'keine Daten' : String(n);
}

function status(s: PdpSuchdaten): { text: string; ton: Ton } {
  const ausserhalbTop8 = (r: PdpRankingWert | null) =>
    r === null || r.position === null || r.position > 8;
  const keinerInTop8 =
    ausserhalbTop8(s.h1Ranking) &&
    ausserhalbTop8(s.produktRanking) &&
    s.aliase.every((a) => ausserhalbTop8(a.ranking));
  if (s.h1PasstZumKeyword === false)
    return { text: 'Keyword passt nicht zum Inhalt', ton: 'schlecht' };
  // Fehlt der Abgleich, ist "Keyword passt" eine Behauptung ohne Grundlage -- und in Gruen
  // irrefuehrend (Story "PDP-Entscheidungsseite: Kleinkram").
  if (s.h1PasstZumKeyword === null) {
    const sichtbarkeit = keinerInTop8 || s.ausloeser ? 'nicht optimal' : 'gut';
    return { text: `Sichtbarkeit ${sichtbarkeit}, Keyword-Abgleich fehlt`, ton: 'mittel' };
  }
  if (keinerInTop8 || s.ausloeser) {
    return { text: 'Keyword passt, Sichtbarkeit nicht optimal', ton: 'mittel' };
  }
  return { text: 'Keyword passt, Sichtbarkeit gut', ton: 'gut' };
}

const ZAHLENKOPF = ['Keyword', 'Position', 'Impressions', 'Klicks'];

function identitaetsZeile(name: string, r: PdpRankingWert | null): string[] {
  if (!r) return [name, '–', 'kein Ranking', '–', '–'];
  return [name, r.keyword ?? '–', position(r.position), anzahl(r.impressions), anzahl(r.klicks)];
}

export function seitentyp(url: string): { typ: string; fremdeDomain?: string } {
  const host = (/^https?:\/\/([^/?#]+)/i.exec(url)?.[1] ?? '').toLowerCase().replace(/^www\./, '');
  // Geschaeftskunden zuerst: der Pfad enthaelt "Produkte" ebenfalls.
  const typ = /\/Geschaeftskunden-Produkte\//i.test(url)
    ? 'PDP Geschäftskunden'
    : /\/Produkte\//i.test(url)
      ? 'PDP'
      : /\/Ratgeber\//i.test(url)
        ? 'Ratgeber'
        : 'Sonstige';
  // Mit Domain-Grenze: endsWith('ergo.de') traf auch supergo.de.
  return host.endsWith('.ergo.de') ? { typ, fremdeDomain: host } : { typ };
}

function kannibalen(k: PdpKannibalisierung): NonNullable<RankingAnzeige['kannibalisierung']> {
  return {
    titel: `Kannibalisierungs-Check${k.fokusKeyword ? ` (Hauptkeyword: „${k.fokusKeyword}“)` : ''}`,
    risiko: k.risiko,
    zeilen: k.eintraege.map((e) => {
      const { typ, fremdeDomain } = seitentyp(e.url);
      const host = /^https?:\/\/([^/?#]+)/i.exec(e.url)?.[1]?.toLowerCase();
      const zeile: KannibalenZeile = {
        position: e.rang === null ? '?' : String(e.rang),
        herkunft: e.herkunft ?? host ?? 'keine Daten',
        typ,
        seite: e.istZiel
          ? { text: 'Diese Seite' }
          : { text: e.titel ?? e.url, ...(e.url ? { url: e.url } : {}) },
        istZiel: e.istZiel,
      };
      if (fremdeDomain) zeile.fremdeDomain = fremdeDomain;
      return zeile;
    }),
  };
}

export function rankingAnzeige(r: PdpRanking): RankingAnzeige {
  const a: RankingAnzeige = {};
  const s = r.suche;
  if (s) {
    a.status = status(s);
    if (s.analyseText) a.analyseText = s.analyseText;
    if (s.top3.length > 0) {
      a.top3 = {
        kopf: ZAHLENKOPF,
        rechts: [1, 2, 3],
        zeilen: s.top3.map((z) => [
          z.keyword ?? '',
          position(z.position),
          anzahl(z.impressions),
          anzahl(z.klicks),
        ]),
      };
    }
    if (s.nonbrand.length > 0) {
      a.nonbrand = {
        kopf: ZAHLENKOPF,
        rechts: [1, 2, 3],
        zeilen: s.nonbrand.map((z) =>
          z.keineDaten
            ? [z.keyword ?? '', 'keine Daten', 'keine Daten', 'keine Daten']
            : [z.keyword ?? '', position(z.position), anzahl(z.impressions), anzahl(z.klicks)],
        ),
      };
    }
    if (s.gscHinweis) a.gscHinweis = s.gscHinweis;
    const h1Norm = s.h1.trim().toLowerCase();
    const zeilen = [
      identitaetsZeile(`H1 „${s.h1}“`, s.h1Ranking),
      identitaetsZeile(`Produkt „${s.produktName}“`, s.produktRanking),
      ...s.aliase
        .filter((x) => x.alias.trim().toLowerCase() !== h1Norm)
        .map((x) => identitaetsZeile(`Alias „${x.alias}“`, x.ranking)),
    ];
    a.identitaet = {
      ueberschrift:
        s.h1PasstZumKeyword === false
          ? 'Wofür die Seite eigentlich ranken sollte (H1, Produktname, Alias)'
          : 'Sichtbarkeit unter eigenen Bezeichnungen (H1, Produktname, Alias)',
      tabelle: {
        kopf: ['Bezeichnung', 'Gefundenes Keyword', 'Position', 'Impressions', 'Klicks'],
        rechts: [2, 3, 4],
        zeilen,
      },
    };
    if (s.fehlRanking) a.fehlRanking = s.fehlRanking;
  }
  if (r.kannibalisierung) a.kannibalisierung = kannibalen(r.kannibalisierung);
  return a;
}

/** Eine Zeile fuer den Seitenkopf: Keyword, Position und Impressionen der H1. */
export function kurzzeile(r?: PdpRanking): string | undefined {
  const h = r?.suche?.h1Ranking;
  if (!h || h.position === null) return undefined;
  const teile = [h.keyword ?? 'H1', `Pos. ${position(h.position)}`];
  if (h.impressions !== null) teile.push(`${h.impressions.toLocaleString('de-DE')} Impressionen`);
  return teile.join(' · ');
}
